import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import InfoTooltip from 'components/_UI/InfoTooltip';
import { Lock } from 'lucide-react';

const STATUS = {
    AVAILABLE: 'disponível',
    SOLD_OUT:  'esgotado',
    DISABLED:  'desativado',
};

export default function CardTickets({ data, onTicketClick = () => false }) {
    const [status, setStatus] = useState('');

    useEffect(() => {
        let disponivel  = false;
        let esgotado    = false;
        let desativado  = false;

        data?.batches?.forEach((el) => {
            if (el?.quantity_available > 0 && el?.is_active == 1)  disponivel  = true;
            if (el?.quantity_available === 0)                       esgotado    = true;
            if (el?.is_active === 0 && el?.quantity_available > 0)  desativado  = true;
        });

        if (disponivel)                      setStatus(STATUS.AVAILABLE);
        else if (esgotado && !disponivel)    setStatus(STATUS.SOLD_OUT);
        else if (desativado)                 setStatus(STATUS.DISABLED);
    }, [data]);

    const renderLots = () =>
        data?.batches?.slice(0, 5).map((el, index) => {
            let loteStatus = '';
            if (el.quantity_available > 0 && el.is_active === 1) loteStatus = STATUS.AVAILABLE;
            if (el.quantity_available === 0)                      loteStatus = STATUS.SOLD_OUT;
            if (el.is_active === 0)                               loteStatus = STATUS.DISABLED;

            return (
                <InfoTooltip
                    key={el.id}
                    bgColor={colors.night}
                    text={
                        <>
                            Lote {index + 1}<br />
                            Valor: {currency.format(el.price)}<br />
                            Quantidade: {el.quantity_available.toLocaleString('pt-BR')}<br />
                            Status: {loteStatus}
                        </>
                    }
                    type='none'
                >
                    <LotDot zindex={index} status={loteStatus}>
                        {index + 1}
                    </LotDot>
                </InfoTooltip>
            );
        });

    return (
        <Card status={status} onClick={onTicketClick}>
            {/* O código fica à vista, não só no tooltip: é o que o produtor manda
                para quem pode comprar. Copiar em um clique fica na aba Ingressos. */}
            {data?.hidden_code && (
                <HiddenAnchor onClick={(e) => e.stopPropagation()}>
                    <InfoTooltip
                        bgColor={colors.night}
                        text={`Ingresso oculto · código ${data.hidden_code} · copie na aba Ingressos`}
                        type='none'
                    >
                        <HiddenBadge>
                            <Lock size={10} strokeWidth={2.5} />
                            <HiddenCode>{data.hidden_code}</HiddenCode>
                        </HiddenBadge>
                    </InfoTooltip>
                </HiddenAnchor>
            )}

            <CardLeft>
                <StatusPill status={status}>{status || '—'}</StatusPill>
                <TicketName status={status}>{data?.name}</TicketName>
            </CardLeft>

            <Notch />

            <CardRight>
                <LotsLabel status={status}>Lotes</LotsLabel>
                <LotRow>
                    {renderLots()}
                    {data?.batches?.length > 5 && (
                        <InfoTooltip
                            bgColor={colors.night}
                            text={`+${data?.batches?.length - 5} lotes`}
                            type='none'
                        >
                            <MoreDot>+{data?.batches?.length - 5}</MoreDot>
                        </InfoTooltip>
                    )}
                </LotRow>
            </CardRight>
        </Card>
    );
}

/* ─────────────────────────────────────────
   Status tokens
───────────────────────────────────────── */
const statusColor = {
    [STATUS.AVAILABLE]: colors.secondaryPurple,
    [STATUS.SOLD_OUT]:  colors.nightLight,
    [STATUS.DISABLED]:  colors.nightLight,
};

const pillColor = {
    [STATUS.AVAILABLE]: { bg: 'rgba(255,255,255,0.18)', text: '#fff' },
    [STATUS.SOLD_OUT]:  { bg: 'rgba(0,0,0,0.12)',       text: '#eee' },
    [STATUS.DISABLED]:  { bg: 'rgba(0,0,0,0.12)',       text: '#ccc' },
};

/* ─────────────────────────────────────────
   Animations
───────────────────────────────────────── */
const lockPulse = keyframes`
    0%, 100% { opacity: 0.8; }
    50%       { opacity: 1;   }
`;

/* ─────────────────────────────────────────
   Card shell — keeps the ticket notch shape
───────────────────────────────────────── */
const Card = styled.div`
    display: flex;
    align-items: stretch;
    position: relative;
    width: 100%;
    min-height: 92px;
    border-radius: 10px;
    cursor: pointer;
    overflow: visible;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    background: ${({ status }) => statusColor[status] || colors.nightLight};
    box-shadow: ${({ status }) =>
        status === STATUS.AVAILABLE
            ? `0 4px 16px ${colors.secondaryPurple}55`
            : '0 2px 8px rgba(0,0,0,0.12)'};
    opacity: ${({ status }) => status === STATUS.SOLD_OUT ? 0.55 : 1};

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${({ status }) =>
            status === STATUS.AVAILABLE
                ? `0 8px 24px ${colors.secondaryPurple}66`
                : '0 6px 18px rgba(0,0,0,0.18)'};
    }

    /* left notch cutout */
    &::before {
        content: '';
        position: absolute;
        left: -10px;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: ${colors.neutralLight};
        z-index: 2;
    }

    /* right notch cutout */
    &::after {
        content: '';
        position: absolute;
        right: -10px;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: ${colors.neutralLight};
        z-index: 2;
    }
`;

/* ─────────────────────────────────────────
   Left / Right halves
───────────────────────────────────────── */
const CardLeft = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    padding: 14px 16px 14px 22px;
    min-width: 0;
`;

const Notch = styled.div`
    width: 1px;
    align-self: stretch;
    margin: 12px 0;
    border-left: 2px dashed rgba(255,255,255,0.3);
    flex-shrink: 0;
`;

const CardRight = styled.div`
    width: 36%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    padding: 14px 18px 14px 16px;
`;

/* ─────────────────────────────────────────
   Status pill + ticket name
───────────────────────────────────────── */
const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    height: 18px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: capitalize;
    background: ${({ status }) => pillColor[status]?.bg || 'rgba(255,255,255,0.18)'};
    color: ${({ status }) => pillColor[status]?.text || '#fff'};
`;

const TicketName = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: ${colors.neutralLight};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: ${({ status }) => status === STATUS.DISABLED ? 0.6 : 1};
`;

/* ─────────────────────────────────────────
   Lots
───────────────────────────────────────── */
const LotsLabel = styled.p`
    margin: 0;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: rgba(255,255,255,0.6);
`;

const LotRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0;
`;

const LotDot = styled.div`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    margin-left: ${({ zindex }) => zindex === 0 ? '0' : '-6px'};
    z-index: ${({ zindex }) => zindex};
    border: 1.5px solid rgba(255,255,255,0.4);
    transition: transform 0.15s ease;
    cursor: help;

    &:hover { transform: scale(1.15); z-index: 20; }

    ${({ status }) => status === STATUS.AVAILABLE && `
        background: #7EFF7B;
        border-color: #038D00;
        color: #038D00;
    `}
    ${({ status }) => status === STATUS.SOLD_OUT && `
        background: rgba(255,255,255,0.15);
        border-color: rgba(255,255,255,0.2);
        color: rgba(255,255,255,0.5);
    `}
    ${({ status }) => status === STATUS.DISABLED && `
        background: rgba(255,255,255,0.1);
        border-color: rgba(255,255,255,0.15);
        color: rgba(255,255,255,0.4);
    `}
`;

const MoreDot = styled(LotDot)`
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.35);
    color: #fff;
    font-size: 9px;
    margin-left: -6px;
    z-index: 15;
`;

/* ─────────────────────────────────────────
   Hidden badge
───────────────────────────────────────── */
const HiddenAnchor = styled.div`
    position: absolute;
    top: 8px;
    right: 14px;
    z-index: 20;
`;

const HiddenCode = styled.span`
    max-width: 110px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: 0.6px;
    text-transform: none;
`;

const HiddenBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px 3px 6px;
    border-radius: 999px;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(6px);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    cursor: help;
    animation: ${lockPulse} 4s ease-in-out infinite;
    transition: transform 0.2s ease;

    &:hover { transform: scale(1.06); }
`;
